
import { Component, Vue, Watch } from 'vue-property-decorator'
import { Getter, Action } from 'vuex-class'
import { CampaignPatch, CampaignSettingsFormData, ExistingId, ImprintDefinition } from '@/types/campaigns'
import MediaPickerDialog from '@/components/MediaPickerDialog.vue'
import SocialPreview from '@/components/SocialPreview.vue'
import SlugNameEdit from '@/components/SlugNameEdit.vue'
import TimeDeltaHuman from '@/components/TimeDeltaHuman.vue'
import StreamingFollow from './StreamingFollow.vue'
import Newsletters from './Newsletters.vue'
import AppHelper from '@/AppHelper'
import DateTimePicker from '@/components/util/DateTimePicker.vue'
import PartyServiceSearchDialog from './PartyServiceSearchDialog.vue'
import { Campaign } from '@/Campaign'
import api from '@/rest'

interface NameSlug {
  name: string
  slug: string
}

interface ExistsParams {
  appId: string
  slug: string
}

@Component({
  components: {
    MediaPickerDialog,
    SlugNameEdit,
    TimeDeltaHuman,
    StreamingFollow,
    SocialPreview,
    DateTimePicker,
    PartyServiceSearchDialog,
    Newsletters
  }
})
export default class CampaignSettings extends Vue {
  @Getter('campaigns/current') campaign!: Campaign
  @Getter('campaigns/editLoading') submitLoading!: boolean
  @Getter('apps/currentHelper') app!: AppHelper
  @Getter('apps/parentHelper') parentApp!: AppHelper|null
  @Getter('apps/currentSlugAround') slugAround!: [string, string]
  @Action('campaigns/EDIT_CURRENT') editCampaign!: (data: CampaignPatch) => Promise<void>
  @Action('campaigns/CHECK_SLUG_EXISTS') slugExists!: (
    data: ExistsParams
  ) => Promise<ExistingId>

  @Action('campaigns/END_CURRENT') endCampaign!: () => Promise<void>
  @Action('campaigns/START_CURRENT') startCampaign!: () => Promise<void>

  valid=true

  // @ts-ignore
  formData: CampaignSettingsFormData = {
    name: '',
    slug: '',
    tag_manager: { artist: null, genre: null, division: null, party_id: null },
    meta_tags: { title: null, og_title: null, og_description: null, og_image: null, favicon: null, twitter_image: null },
    test: false,
    newsletters: [],
    follow: [],
    starts_at: null,
    ends_at: null,
    imprint_id: 'umg_de',
    base_url_id: null,
    daily_streams: { resets_at: '00:00:00', limit: null }
  }

  unsavedChanges = false
  watcherOn = true
  slugDuplicateId: string | null = null
  imprints: ImprintDefinition[] = []
  selectedBaseUrlId = ''

  @Watch('selectedBaseUrlId')
  onBaseUrlIdChanged (val: string) {
    this.formData.base_url_id = val || null
  }

  @Watch('formData', { deep: true, immediate: true })
  onPropertyChanged (newValue: CampaignPatch, oldValue: CampaignPatch) {
    if (!this.watcherOn || this.unsavedChanges || !oldValue || !Object.keys(oldValue).length) {
      return
    }
    this.unsavedChanges = true
  }

  async created () {
    await this.loadImprints()
    this.reloadData()
  }

  async loadImprints () {
    this.imprints = await api.settings.getImprints()
  }

  get updatedAt () {
    return new Date(this.campaign.data.updated_at)
  }

  get currentImprintName (): string | null {
    if (this.campaign.data.imprint) {
      return this.campaign.data.imprint.name
    }
    return null
  }

  reloadData () {
    this.watcherOn = false
    const c = this.campaign
    this.formData = {
      name: c.data.name,
      slug: c.data.slug,
      tag_manager: { ...c.data.tag_manager },
      meta_tags: { ...c.data.meta_tags },
      test: c.data.test,
      newsletters: [...JSON.parse(JSON.stringify(c.data.newsletters))],
      follow: JSON.parse(JSON.stringify(c.data.follow)),
      starts_at: c.data.starts_at,
      ends_at: c.data.ends_at,
      imprint_id: c.data.imprint_id,
      base_url_id: c.data.base_url_id || null,
      // The server returns "HH:MM:SS" with optional tz suffix (e.g. "00:00:00Z").
      // Strip any non time-of-day suffix so the HTML5 <input type="time"> control
      // can round-trip the value cleanly.
      daily_streams: {
        resets_at: (c.data.daily_streams?.resets_at || '00:00:00').slice(0, 8),
        limit: c.data.daily_streams?.limit ?? null
      }
    }
    if (this.app.hasMultipleBaseUrls) {
      this.selectedBaseUrlId = c.data.base_url_id || this.app.baseUrls.default_id || ''
    }
    this.$nextTick(() => { this.watcherOn = true })
  }

  get nameAndSlug (): NameSlug {
    return { slug: this.formData.slug || '', name: this.formData.name || '' }
  }

  set nameAndSlug (value: NameSlug) {
    this.formData.name = value.name
    this.formData.slug = value.slug
    this.validateSlug(value.slug)
  }

  // --- Daily stream cap helpers ---
  // The "enforce cap" toggle drives whether `limit` is null (off) or numeric (on).
  get dailyStreamLimitEnabled (): boolean {
    return this.formData.daily_streams.limit !== null
  }

  set dailyStreamLimitEnabled (enabled: boolean) {
    this.formData.daily_streams = {
      ...this.formData.daily_streams,
      limit: enabled ? (this.formData.daily_streams.limit ?? 0) : null
    }
  }

  get dailyStreamLimitValue (): number | null {
    return this.formData.daily_streams.limit
  }

  set dailyStreamLimitValue (value: number | null) {
    this.formData.daily_streams = {
      ...this.formData.daily_streams,
      // null/NaN keeps the field empty; toggle setter handles the no-cap case.
      limit: value === null || Number.isNaN(value as number) ? null : Number(value)
    }
  }

  // Save is gated on this: when the cap is enabled, the limit must be a positive
  // integer. The SDK rejects `limit <= 0` with a `gt=0` constraint.
  get dailyStreamLimitInvalid (): boolean {
    if (!this.dailyStreamLimitEnabled) {
      return false
    }
    const v = this.dailyStreamLimitValue
    return v === null || Number.isNaN(v as number) || v <= 0
  }

  get dailyStreamResetsAt (): string {
    return this.formData.daily_streams.resets_at
  }

  set dailyStreamResetsAt (value: string) {
    // HTML5 time picker may emit "HH:MM" (no seconds); the server accepts both
    // but normalise to "HH:MM:SS" for consistency.
    const normalised = value && value.length === 5 ? `${value}:00` : (value || '00:00:00')
    this.formData.daily_streams = {
      ...this.formData.daily_streams,
      resets_at: normalised
    }
  }

  get computedSlugAround (): [string, string] {
    if (!this.app.hasMultipleBaseUrls) {
      return this.slugAround
    }
    const baseUrlItem = this.app.baseUrls.available.find(
      bu => bu.id === this.selectedBaseUrlId
    )
    const baseUrl = baseUrlItem ? baseUrlItem.url : ''
    const tmpl = baseUrl + this.app.config.urls.campaign_public
    return tmpl.split('{slug}') as [string, string]
  }

  onSlugEditToggled (state: boolean) {
    if (state) {
      this.slugDuplicateId = null
    }
  }

  async validateSlug (slug: string) {
    if (!slug) {
      return
    }
    const result = await this.slugExists({ appId: this.campaign.data.app, slug })
    if (!result.exists || result.existing_id === this.campaign.data.id) {
      return
    }
    this.slugDuplicateId = result.existing_id
  }

  async startCampaignNow () {
    if (!confirm('The campaign will start immediately. Are you sure?')) {
      return
    }
    await this.startCampaign()
    this.reloadData()
  }

  async endCampaignNow () {
    if (!confirm('The campaign will end immediately. Are you sure?')) {
      return
    }
    await this.endCampaign()
    this.reloadData()
  }

  async submit () {
    const form: any = this.$refs.form
    if (!form.validate()) {
      return
    }
    await this.editCampaign(this.formData)
    this.reloadData()
    this.unsavedChanges = false
  }
}
